import { Controller } from "@hotwired/stimulus"

// List of 30 logos to rotate through
const LOGO_DOMAINS = [
  "stripe.com",
  "figma.com",
  "zapier.com",
  "dropbox.com",
  "youtube.com",
  "openai.com",
  "amazon.com",
  "airbnb.com",
  "canva.com",
  "shopify.com",
  "microsoft.com",
  "google.com",
  "apple.com",
  "meta.com",
  "netflix.com",
  "spotify.com",
  "twitter.com",
  "linkedin.com",
  "github.com",
  "slack.com",
  "notion.so",
  "asana.com",
  "trello.com",
  "atlassian.com",
  "salesforce.com",
  "hubspot.com",
  "zoom.us",
  "adobe.com",
  "nvidia.com",
  "tesla.com"
]

// Interval between logo changes in milliseconds
const INTERVAL = 2000

export default class extends Controller {

  connect() {
    this.prefetchedImages = new Map()
    this.cellsWithImages = []
    this.currentLogos = new Map() // Track current logo for each cell

    // Find all cells that contain images
    this.findCellsWithImages()

    // Prefetch all logos
    this.prefetchLogos()

    // Start rotation after prefetching
    this.startRotation()
  }

  disconnect() {
    if (this.rotationInterval) {
      clearInterval(this.rotationInterval)
    }
  }

  findCellsWithImages() {
    const grids = this.element.querySelectorAll(".logo-api-hero-logos-grid")

    grids.forEach(grid => {
      const cells = grid.querySelectorAll(".logo-api-hero-logos-grid__cell")
      cells.forEach(cell => {
        const img = cell.querySelector("img")
        if (img) {
          const cellId = `${grid.className}-${this.cellsWithImages.length}`

          // Extract current logo domain from existing image src
          // Format: https://logos.hunter.io/{domain}
          const currentSrc = img.src
          const logoDomainMatch = currentSrc.match(/logos\.hunter\.io\/([^/]+)/)
          const currentLogoDomain = logoDomainMatch ? logoDomainMatch[1] : null

          this.cellsWithImages.push({ cell, img, cellId })

          // Store the current logo if we found one
          if (currentLogoDomain) {
            this.currentLogos.set(cellId, currentLogoDomain)
          }
        }
      })
    })
  }

  prefetchLogos() {
    LOGO_DOMAINS.forEach(domain => {
      const logoUrl = `https://logos.hunter.io/${domain}`
      const img = new Image()
      img.onload = () => {
        // Image successfully loaded and cached
      }
      img.onerror = () => {
        // Silently handle errors - logo might not be available
      }
      img.src = logoUrl
      this.prefetchedImages.set(domain, img)
    })
  }

  startRotation() {
    // Start rotating one random logo at a time
    // Existing logos from HTML are already tracked in findCellsWithImages()
    this.rotationInterval = setInterval(() => {
      this.updateRandomLogo()
    }, INTERVAL)
  }

  updateRandomLogo() {
    if (this.cellsWithImages.length === 0) return

    // Filter cells that have a tracked logo (skip empty cells)
    const cellsWithLogos = this.cellsWithImages.filter(({ cellId }) =>
      this.currentLogos.has(cellId)
    )

    if (cellsWithLogos.length === 0) return

    // Randomly select one cell to update
    const randomIndex = Math.floor(Math.random() * cellsWithLogos.length)
    const { img, cellId } = cellsWithLogos[randomIndex]

    // Get current logo for this cell
    const currentLogo = this.currentLogos.get(cellId)

    // Get all currently visible logos (excluding the one in the cell we're updating)
    const visibleLogos = new Set()
    this.currentLogos.forEach((logo, id) => {
      if (id !== cellId) {
        visibleLogos.add(logo)
      }
    })

    // Get a random logo that's different from the current one and not visible elsewhere
    const availableLogos = LOGO_DOMAINS.filter(
      logo => logo !== currentLogo && !visibleLogos.has(logo)
    )

    // If no available logos (shouldn't happen with 30 logos), fall back to any logo except current
    if (availableLogos.length === 0) {
      availableLogos.push(...LOGO_DOMAINS.filter(logo => logo !== currentLogo))
    }

    const newLogoDomain = availableLogos[Math.floor(Math.random() * availableLogos.length)]
    const logoUrl = `https://logos.hunter.io/${newLogoDomain}`

    // Update the stored logo for this cell
    this.currentLogos.set(cellId, newLogoDomain)

    // Use prefetched image if available, otherwise create new one
    const prefetchedImg = this.prefetchedImages.get(newLogoDomain)

    if (prefetchedImg && prefetchedImg.complete) {
      // Image is already loaded, do smooth transition
      this.fadeTransition(img, logoUrl)
    } else {
      // Wait for image to load, then transition
      const newImg = new Image()
      newImg.onload = () => {
        this.fadeTransition(img, logoUrl)
      }
      newImg.src = logoUrl
    }
  }

  fadeTransition(img, newSrc) {
    // Fade out, change, fade in
    img.style.opacity = "0"
    img.style.filter = "blur(6px)"

    // After fade out completes, change source and fade in
    setTimeout(() => {
      // Only change src if it's different to avoid unnecessary reloads
      if (img.src !== newSrc) {
        img.src = newSrc
      }
      // Small delay to ensure src change is processed, then fade in
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          img.style.opacity = "1"
          img.style.filter = "blur(0px)"
        })
      })
    }, 300)
  }

}
